import {
  CameraDeniedError,
  type ScanOptions,
  type ScanSession,
  type Scanner,
  type ZoomRange,
} from '@/application/ports/Scanner';
import { DECODE_MAX_SIDE, scanRegion } from './frame';
import type { DecodeRequest, DecodeResponse } from './qrWorker';

/**
 * The camera scanner. Section 11.3, rebuilt by the request of 4 October 2026
 * (second), section 5, to be quick on an iPhone in ordinary lab light.
 *
 * - The rear camera at 1920 by 1080 where it will give it, with continuous
 *   autofocus where the camera offers it, started the moment the screen opens.
 * - Only the centred square under the on-screen frame is decoded, scaled to
 *   at most DECODE_MAX_SIDE a side: a fraction of the work of a whole frame.
 * - `BarcodeDetector` on Android Chrome; elsewhere, iPhone above all, ZXing's
 *   C++ reader in WebAssembly in a worker, alternate frames with the contrast
 *   stretched, which is what reads a grey card in a dim corner.
 * - One decode in flight at a time and the next frame straight after it, so
 *   it scans as fast as the phone decodes, several times a second, and never
 *   queues stale frames.
 *
 * It used to hand the whole 1280 by 720 frame to the JavaScript port of ZXing
 * on the main thread on iPhone, with the default fixed threshold, which is why
 * it wanted a bright room and made the preview stutter.
 */

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}

interface BarcodeDetectorConstructor {
  new (options: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

function nativeDetector(): BarcodeDetectorConstructor | null {
  const candidate = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
  return candidate ?? null;
}

/** Whether the native detector is there and reads QR codes. Asked once. */
let nativeQr: Promise<boolean> | null = null;
function hasNativeQr(): Promise<boolean> {
  nativeQr ??= (async () => {
    const Detector = nativeDetector();
    if (Detector === null) return false;
    try {
      const formats = (await Detector.getSupportedFormats?.()) ?? ['qr_code'];
      return formats.includes('qr_code');
    } catch {
      return false;
    }
  })();
  return nativeQr;
}

/** One worker for the life of the page, made early so the WebAssembly is ready when the camera is. */
let worker: Worker | null = null;
let nextId = 1;
const waiting = new Map<number, (response: DecodeResponse) => void>();

function decoder(): Worker {
  if (worker === null) {
    worker = new Worker(new URL('./qrWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<DecodeResponse>) => {
      waiting.get(event.data.id)?.(event.data);
      waiting.delete(event.data.id);
    };
  }
  return worker;
}

function decodeInWorker(image: ImageData, enhance: boolean, thorough: boolean): Promise<DecodeResponse> {
  return new Promise((resolve) => {
    const id = nextId++;
    waiting.set(id, resolve);
    const request: DecodeRequest = { id, image, enhance, thorough };
    decoder().postMessage(request, [image.data.buffer]);
  });
}

/** Rear camera, high resolution, focusing on its own. */
const CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { ideal: 30 },
  },
  audio: false,
};

/** Digital zoom, where the camera has none of its own. */
const DIGITAL_ZOOM: ZoomRange = { min: 1, max: 4, step: 0.1, hardware: false };

/** Never decode more often than this, so a fast phone does not cook its battery. */
const MIN_FRAME_MS = 80;

interface TrackCapabilities {
  torch?: boolean;
  zoom?: { min: number; max: number; step?: number };
  focusMode?: string[];
}

export class CameraScanner implements Scanner {
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia !== undefined;
  }

  async start(options: ScanOptions): Promise<ScanSession> {
    // The decoder loads while the camera starts, rather than after it.
    const native = hasNativeQr();
    void native.then((yes) => {
      if (!yes) decoder();
    });

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);
    } catch (error) {
      // A refusal is a different thing from a broken camera: the screen offers
      // manual entry either way, but only a refusal is worth explaining.
      if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'SecurityError')) {
        throw new CameraDeniedError();
      }
      throw error;
    }

    const video = options.video;
    video.srcObject = stream;
    video.setAttribute('playsinline', 'true');
    video.muted = true;
    await video.play();

    const track = stream.getVideoTracks()[0] ?? null;
    const capabilities = (track?.getCapabilities?.() ?? {}) as TrackCapabilities;

    // Continuous autofocus where the camera offers it; iPhone focuses on its own.
    if (track !== null && capabilities.focusMode?.includes('continuous') === true) {
      await track
        .applyConstraints({ advanced: [{ focusMode: 'continuous' }] } as unknown as MediaTrackConstraints)
        .catch(() => undefined);
    }

    const torchAvailable = capabilities.torch === true;
    const hardwareZoom = capabilities.zoom !== undefined && capabilities.zoom.max > capabilities.zoom.min;
    const zoom: ZoomRange = hardwareZoom && capabilities.zoom !== undefined
      ? { min: capabilities.zoom.min, max: capabilities.zoom.max, step: capabilities.zoom.step ?? 0.1, hardware: true }
      : DIGITAL_ZOOM;

    const state = { stopped: false, paused: false, digitalZoom: 1, frame: 0 };
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const useNative = await native;
    const Detector = nativeDetector();
    const detector = useNative && Detector !== null ? new Detector({ formats: ['qr_code'] }) : null;

    const next = () => {
      if (state.stopped) return;
      const withFrames = video as HTMLVideoElement & {
        requestVideoFrameCallback?: (callback: () => void) => number;
      };
      if (typeof withFrames.requestVideoFrameCallback === 'function') withFrames.requestVideoFrameCallback(tick);
      else requestAnimationFrame(tick);
    };

    let last = 0;
    const tick = () => {
      if (state.stopped) return;
      const now = performance.now();
      if (state.paused || now - last < MIN_FRAME_MS || video.readyState < video.HAVE_CURRENT_DATA || context === null) {
        next();
        return;
      }
      last = now;

      const region = scanRegion(video.videoWidth, video.videoHeight, state.digitalZoom);
      const side = Math.min(region.side, DECODE_MAX_SIDE);
      canvas.width = side;
      canvas.height = side;
      context.drawImage(video, region.x, region.y, region.side, region.side, 0, 0, side, side);
      state.frame += 1;

      const started = performance.now();
      const decoded: Promise<string | null> =
        detector !== null
          ? detector.detect(canvas).then((found) => found[0]?.rawValue ?? null)
          : decodeInWorker(context.getImageData(0, 0, side, side), state.frame % 2 === 0, false).then(
              (response) => response.text,
            );

      decoded
        .then((value) => {
          options.onDecodeTime?.(performance.now() - started);
          if (value !== null && value !== '' && !state.stopped && !state.paused) options.onResult(value);
        })
        .catch((error: unknown) => options.onError(error))
        .finally(next);
    };
    next();

    return {
      torchAvailable,
      setTorch: async (on: boolean) => {
        if (!torchAvailable || track === null) return;
        // `torch` is a real constraint on Android Chrome and newer iPhones, and
        // absent from the DOM typings, which describe the standardised set only.
        await track.applyConstraints({ advanced: [{ torch: on }] } as unknown as MediaTrackConstraints);
      },
      zoom,
      setZoom: async (value: number) => {
        const clamped = Math.min(zoom.max, Math.max(zoom.min, value));
        if (zoom.hardware && track !== null) {
          await track
            .applyConstraints({ advanced: [{ zoom: clamped }] } as unknown as MediaTrackConstraints)
            .catch(() => undefined);
        } else {
          state.digitalZoom = clamped;
        }
      },
      pause: () => {
        state.paused = true;
      },
      resume: () => {
        state.paused = false;
      },
      stop: () => {
        state.stopped = true;
        for (const t of stream.getTracks()) t.stop();
        video.srcObject = null;
      },
    };
  }

  async decodeImage(file: Blob): Promise<string | null> {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) return null;
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    if (await hasNativeQr()) {
      const Detector = nativeDetector();
      if (Detector !== null) {
        const found = await new Detector({ formats: ['qr_code'] }).detect(canvas).catch(() => []);
        if (found[0]?.rawValue) return found[0].rawValue;
      }
    }
    // As taken, then with the contrast stretched: a photograph is the only chance.
    const plain = await decodeInWorker(context.getImageData(0, 0, width, height), false, true);
    if (plain.text !== null) return plain.text;
    const enhanced = await decodeInWorker(context.getImageData(0, 0, width, height), true, true);
    return enhanced.text;
  }
}
